// „Разбор сетапа" — analiza scrisă de algoritm, nu de un model de limbaj.
//
// Aceleași patru întrebări pe care și le pune un scalper înainte de intrare, fiecare
// răspunsă din cifrele de pe ecran și refăcută la fiecare citire nouă a datelor:
//
//   Что происходит — cine e mai puternic acum (fluxul de pe 4 burse, ordinele mari, OI);
//   Что подтвердит — ce trebuie să se întâmple ca intrarea să fie justificată;
//   Где ошибка    — stopul, plus avertismentele de amplasare: stop chiar în fața unei
//                   plotnosti (o lovesc și te scot), plotnoste pe drumul spre țintă,
//                   absorbție împotrivă, suport fals retras;
//   Вердикт       — din speranța matematică a modelului și din ce se vede acum.
//
// Regulile sunt scrise explicit mai jos; fiecare propoziție are în spate o condiție pe
// care o poți citi, nu o „intuiție".

import { fmtPrice } from '../core/util.js';
import { money } from '../chart/whales.js';

const NUME = { binance: 'Binance', bybit: 'Bybit', okx: 'OKX', bitget: 'Bitget' };
const pct = (x) => `${x >= 0 ? '+' : ''}${(x * 100).toFixed(0)}%`;

export function analyzeSetup(it, feed) {
  const now = [];
  const confirm = [];
  const invalid = [];
  const p = it.plan;
  const lung = it.direction === 'long';
  const s = lung ? 1 : -1;
  const pret = feed.lastPrice || it.price;
  const f = feed.flow || it.flow;
  const g = it.grade || {};

  // ---------------------------------------------------------- ce se întâmplă
  const strans = f && f.warm >= 5 && f.vol5 >= 25000;
  let fluxPro = false;
  let fluxContra = false;
  if (strans && f.delta5 !== null && f.delta5 !== undefined) {
    const d = f.delta5;
    fluxPro = d * s >= 0.10;
    fluxContra = d * s <= -0.10;
    now.push(Math.abs(d) < 0.05
      ? `Поток 4 бирж за 5 минут в балансе (${pct(d)}): явного перевеса нет.`
      : `За 5 минут по 4 биржам сильнее ${d > 0 ? 'покупатели' : 'продавцы'} (${pct(d)}) — это ${fluxPro ? 'в сторону сделки' : fluxContra ? 'против сделки' : 'слабо'}.`);
  } else {
    now.push(`Поток 4 бирж ещё собирается (${f?.warm ?? 0}/5 мин) — сила сторон пока не определена.`);
  }
  const ultime = (feed.prints || []).filter((x) => Date.now() / 1000 - x.t <= 900);
  if (ultime.length >= 3) {
    const c = ultime.filter((x) => x.side === 'buy').reduce((a, x) => a + x.usd, 0);
    const v = ultime.filter((x) => x.side === 'sell').reduce((a, x) => a + x.usd, 0);
    now.push(`Крупные рыночные ордера за 15 минут: покупки ${money(c)}, продажи ${money(v)}.`);
  }
  if (f?.oi15 !== null && f?.oi15 !== undefined && Math.abs(f.oi15) >= 0.5) {
    now.push(f.oi15 > 0
      ? `Открытый интерес +${f.oi15}% за 15 минут — в движение входят новые деньги.`
      : `Открытый интерес ${f.oi15}% за 15 минут — позиции закрываются, движение без новых денег.`);
  }

  if (it.kind === 'approach' && it.plans) return analyzeApproach(it, feed, { now, confirm, invalid });
  if (!p) return { now, confirm, invalid, verdict: { cls: 'skip', text: 'Пропустить: у сетапа нет плана.' } };

  // ---------------------------------------------------------- ziduri pe drum
  const risc = Math.abs(p.entry - p.stop);
  const vii = (feed.walls || []).filter((w) => !w.gone);
  const intre = (x, a, b) => x > Math.min(a, b) && x < Math.max(a, b);
  // Zidul din partea opusă, între intrare și țintă: obstacolul spre profit.
  const obstacol = vii
    .filter((w) => w.side === (lung ? 'ask' : 'bid') && intre(w.price, p.entry, p.target))
    .sort((a, b) => Math.abs(a.price - p.entry) - Math.abs(b.price - p.entry))[0];
  // Zidul de partea noastră, chiar dincolo de stop: stopul stă în fața lui.
  const subStop = vii
    .filter((w) => w.side === (lung ? 'bid' : 'ask')
      && (lung ? w.price < p.stop && w.price >= p.stop - 0.5 * risc
        : w.price > p.stop && w.price <= p.stop + 0.5 * risc))
    .sort((a, b) => b.usd - a.usd)[0];
  // Zidul de partea noastră între stop și intrare: protejează poziția.
  const scut = vii
    .filter((w) => w.side === (lung ? 'bid' : 'ask') && intre(w.price, p.stop, p.entry))
    .sort((a, b) => b.usd - a.usd)[0];
  const falsSuport = (feed.walls || [])
    .filter((w) => w.state === 'pulled' && w.side === (lung ? 'bid' : 'ask'))
    .sort((a, b) => b.peak - a.peak)[0];
  const absContra = (feed.absorption || []).find((a) => a.kind === (lung ? 'absorb_buy' : 'absorb_sell')
    && intre(a.price, p.entry, p.target));
  const absPro = (feed.absorption || []).find((a) => a.kind === (lung ? 'absorb_sell' : 'absorb_buy')
    && intre(a.price, p.stop, p.entry));

  // ---------------------------------------------------------- confirmare
  const lvl = fmtPrice(it.level);
  if (it.kind === 'squeeze') {
    confirm.push(`Пробой ${fmtPrice(p.entry)} — вход стоп-ордером, без пробоя сделки нет.`);
  } else if (it.kind === 'breakout') {
    confirm.push(`Цена держится ${lung ? 'выше' : 'ниже'} уровня ${lvl}: 1-минутные свечи не закрываются обратно за ним.`);
  } else if (it.kind === 'bounce') {
    confirm.push(`Уровень ${lvl} держит: нет закрытия 1-минутной свечи ${lung ? 'ниже' : 'выше'} него.`);
  } else if (it.kind === 'impulse') {
    confirm.push(`Лимитный ордер на откате к ${fmtPrice(p.entry)} — середина импульсной свечи. Не догоняйте свечу: без отката сделки нет.`);
    confirm.push(`Откат идёт на меньшем объёме, чем импульс, и не закрывается ${lung ? 'ниже' : 'выше'} основания ${lvl}.`);
  } else if (it.kind === 'false_breakout') {
    confirm.push(`Цена остаётся ${lung ? 'выше' : 'ниже'} ${lvl} после ложного выхода — ловушка закрылась.`);
  }
  if (obstacol) {
    confirm.push(obstacol.state === 'eating'
      ? `Плотность ${money(obstacol.usd)} на ${fmtPrice(obstacol.price)} (${NUME[obstacol.ex]}) уже разъедается — это и есть подтверждение.`
      : `На пути к цели стоит плотность ${money(obstacol.usd)} на ${fmtPrice(obstacol.price)} (${NUME[obstacol.ex]}): дождитесь, пока её начнут разъедать.`);
  }
  if (!fluxPro) {
    confirm.push(`Поток 4 бирж за 5 минут должен стать ${lung ? 'покупательским' : 'продавцовым'} (дельта ${lung ? '≥ +10%' : '≤ −10%'}).`);
  }

  // ---------------------------------------------------------- unde e greșit
  invalid.push(`Стоп ${fmtPrice(p.stop)} (${(Math.abs(p.stop - p.entry) / p.entry * 100).toFixed(2)}%): за ним сетап неверен.`);
  if (subStop) {
    const nou = lung ? subStop.price - 0.1 * risc : subStop.price + 0.1 * risc;
    const riscNou = Math.abs(p.entry - nou);
    invalid.push(`Стоп стоит прямо перед плотностью ${money(subStop.usd)} на ${fmtPrice(subStop.price)} (${NUME[subStop.ex]}): `
      + `цену может «подтянуть» к ней и выбить стоп. Надёжнее стоп за плотностью — ${fmtPrice(nou)} `
      + `(риск ×${(riscNou / risc).toFixed(2)}).`);
  } else if (scut) {
    invalid.push(`Между входом и стопом стоит плотность ${money(scut.usd)} на ${fmtPrice(scut.price)} (${NUME[scut.ex]}) — она защищает позицию. Если её снимут или съедят, сетап слабеет.`);
  }
  if (absContra) {
    invalid.push(`На пути к цели поглощение ${lung ? 'покупок' : 'продаж'} ${money(absContra.usd)} на ${fmtPrice(absContra.price)}: там крупный встречный игрок.`);
  }
  if (absPro) {
    invalid.push(`За нами поглощение ${lung ? 'продаж' : 'покупок'} ${money(absPro.usd)} на ${fmtPrice(absPro.price)} — крупный игрок защищает уровень. Пробой этой зоны отменяет сетап.`);
  }
  if (falsSuport) {
    invalid.push(`Плотность ${money(falsSuport.peak)} на ${fmtPrice(falsSuport.price)} (${NUME[falsSuport.ex]}) сняли до подхода цены — та «поддержка» была фальшивой.`);
  }

  // ---------------------------------------------------------- verdict
  const e = g.E;
  const asteptare = e === null || e === undefined ? ''
    : ` Ожидание по модели ${e >= 0 ? '+' : ''}${e.toFixed(2)}R после комиссии${g.n ? ` (похожих сигналов: ${g.n})` : ''}.`;
  let verdict;
  if (g.grade === 'C' || fluxContra) {
    verdict = { cls: 'skip', text: `Пропустить.${fluxContra ? ' Поток 4 бирж против сделки.' : ''}${asteptare}` };
  } else if (obstacol && obstacol.state !== 'eating' && Math.abs(obstacol.price - p.entry) <= risc * 1.2) {
    verdict = { cls: 'wait', text: `Ждать: плотность ${money(obstacol.usd)} в пределах 1R от входа ещё не разъедается.${asteptare}` };
  } else if (g.grade === 'A' && (fluxPro || !strans)) {
    verdict = { cls: 'go', text: `Входить по плану.${asteptare}` };
  } else {
    verdict = { cls: 'wait', text: `Ждать подтверждения из пункта выше.${asteptare}` };
  }
  if (pret && p.type === 'market' && Math.abs(pret - p.entry) > risc * 0.5) {
    verdict.text += ` Цена уже ушла от входа на ${(Math.abs(pret - p.entry) / risc).toFixed(1)}R — по рынку соотношение хуже плана.`;
    if (verdict.cls === 'go') verdict.cls = 'wait';
  }
  return { now, confirm, invalid, verdict };
}

/**
 * Apropierea de nivel: nu alegem dinainte direcția (măsurat — balanța nu se poate muta),
 * ci spunem ce semn din stakan alege între cele două planuri, în clipa atingerii.
 */
function analyzeApproach(it, feed, { now, confirm, invalid }) {
  const rez = it.level_kind === 'resistance';
  const L = it.level;
  const pret = feed.lastPrice || it.price;
  const vii = (feed.walls || []).filter((w) => !w.gone);
  // Zidul de pe nivel: de partea care apără nivelul (ask la rezistență, bid la suport),
  // la cel mult 0.3% de el.
  const laNivel = vii
    .filter((w) => w.side === (rez ? 'ask' : 'bid') && Math.abs(w.price - L) / L <= 0.003)
    .sort((a, b) => b.usd - a.usd)[0];
  const retras = (feed.walls || [])
    .filter((w) => w.state === 'pulled' && w.side === (rez ? 'ask' : 'bid') && Math.abs(w.price - L) / L <= 0.003)
    .sort((a, b) => b.peak - a.peak)[0];
  const dist = pret ? Math.abs(L - pret) / pret * 100 : null;

  now.unshift(`Цена ${dist !== null ? `в ${dist.toFixed(2)}% ` : ''}от ${rez ? 'сопротивления' : 'поддержки'} ${fmtPrice(L)} (касаний: ${it.touches}).`);
  if (laNivel) {
    now.push(`На уровне стоит плотность ${money(laNivel.usd)} (${NUME[laNivel.ex]}), ${laNivel.state === 'eating' ? 'и её уже разъедают' : 'пока держится'}.`);
  } else {
    now.push('На самом уровне крупной плотности в стаканах 4 бирж сейчас нет.');
  }

  confirm.push(`План пробоя: ${laNivel ? `плотность ${money(laNivel.usd)} на уровне начинают разъедать` : 'цена проходит уровень с ускорением'}, поток 4 бирж ${rez ? 'покупательский' : 'продавцовый'} — тогда стоп-ордер ${fmtPrice(it.plans.breakout.entry)}.`);
  confirm.push(`План отскока: ${laNivel ? `плотность ${money(laNivel.usd)} стоит, в неё бьют, а она не уменьшается` : 'цена вязнет у уровня, крупные сделки против движения'} — тогда лимит ${fmtPrice(it.plans.bounce.entry)}.`);

  invalid.push(`Пробой отменяется закрытием обратно за ${fmtPrice(it.plans.breakout.stop)}; отскок — пробоем ${fmtPrice(it.plans.bounce.stop)}.`);
  if (retras) {
    invalid.push(`Плотность ${money(retras.peak)} на уровне сняли (${NUME[retras.ex]}) — уровень защищали для вида. Отскок здесь опасен.`);
  }

  let verdict;
  if (laNivel?.state === 'eating') {
    verdict = { cls: 'go', text: `Готовьте план пробоя: плотность на уровне уже разъедают.` };
  } else if (retras) {
    verdict = { cls: 'wait', text: 'Ждать: защиту уровня сняли — отскок ненадёжен, пробой ещё не начался.' };
  } else if (laNivel && laNivel.age >= 120) {
    verdict = { cls: 'wait', text: `Ждать касания: плотность ${money(laNivel.usd)} стоит ${Math.floor(laNivel.age / 60)} мин. Если в неё будут бить и она устоит — план отскока; если начнут разъедать — план пробоя.` };
  } else {
    verdict = { cls: 'wait', text: 'Ждать касания и смотреть стакан: заранее направление не известно (по истории 46% пробой / 53% отскок).' };
  }
  const st = it.stats;
  if (st?.breakout?.E !== undefined && st?.breakout?.E !== null) {
    verdict.text += ` Вслепую оба плана в истории убыточны (пробой ${st.breakout.E.toFixed(2)}R, отскок ${st.bounce.E.toFixed(2)}R) — входите только по сигналу стакана.`;
  }
  return { now, confirm, invalid, verdict };
}
